/**
 * COMPONENTE DE INICIO DE SESIÓN
 *
 * Versión modificada: se agrega salida JSON de sesión que contiene
 * los permisos del rol obtenido desde config/roleRoutes.js.
 *
 * Cambios relevantes:
 * - Importa getRoleConfig desde config/roleRoutes para obtener permisos.
 * - Tras login exitoso crea `sessionJson` con { user, role, permissions, dashboardRoute }.
 * - Guarda `sessionJson` en localStorage bajo la clave "session" para uso del cliente.
 * - Envía `sessionJson` vía state en navigate para que la página destino pueda leerlo.
 *
 * Motivo:
 * El requerimiento fue: "cuando un rol se hace login me diga en el json que permisos tiene cada rol,
 * como en el tema que se implemento en la clase roleRoutes.js" — esta versión aplica esa lógica
 * en el flujo de login del componente.
 *
 * NOTA: Este archivo asume que `login()` del context devuelve un objeto con información del usuario
 * (por ejemplo { user: { id, name, email, ... }, token, ... }). Si tu authService devuelve otra
 * estructura, ajusta la construcción de sessionJson según corresponda.
 *
 * Referencias:
 * - roleRoutes.js: configuración de roles y permisos (usada para obtener permisos) :contentReference[oaicite:2]{index=2}
 * - index.js original: base del componente y UI (esta versión extiende esa base) :contentReference[oaicite:3]{index=3}
 *
 * @component
 * @returns {JSX.Element}
 * @author Diego
 * @version 4.1-mod
 * @since 2025-09
 */

import { useState, useEffect } from "react";

// react-router-dom components
import { Link, useNavigate, useLocation } from "react-router-dom";

// Authentication context (tu implementación)
import { useAuth } from "context/authContext";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/Login/imagen1_optimizada.webp";

// Configuración centralizada de roles
// Importa las funciones de roleRoutes: getRedirectAfterLogin, isValidRole y getRoleConfig
// getRoleConfig es la que nos dará el array de permisos del rol
import { getRedirectAfterLogin, isValidRole, getRoleConfig } from "config/roleRoutes";

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState(""); // Para candidatos
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [errors, setErrors] = useState({ email: "", username: "", password: "", role: "" });
  const [isValid, setIsValid] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [touched, setTouched] = useState({ email: false, username: false, password: false, role: false });

  // Estados para manejo de errores específicos
  const [customError, setCustomError] = useState("");

  // Auth context
  const { login, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Ruta desde donde llegó el usuario (para posible redirección)
  const from = location.state?.from?.pathname || "/";

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // Determina si mostrar email o username según el rol seleccionado
  const shouldShowEmail = () => {
    return role === "admin" || role === "empresa" || role === "evaluador";
  };

  const shouldShowUsername = () => {
    return role === "candidato";
  };

  const getCurrentIdentifier = () => {
    return shouldShowEmail() ? email : username;
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
    setTouched((prev) => ({ ...prev, role: true }));
    // Limpiar errores de auth al cambiar
    if (error || customError) {
      setShowAlert(false);
      setCustomError("");
      clearError();
    }
  };

  const validate = () => {
    const newErrors = { email: "", username: "", password: "", role: "" };
    let valid = true;

    // Validación de rol
    if (!role && touched.role) {
      newErrors.role = "El rol es obligatorio";
      valid = false;
    } else if (role && !isValidRole(role)) {
      newErrors.role = "Rol no válido";
      valid = false;
    }

    // Validaciones dinámicas por rol
    if (role) {
      if (shouldShowEmail()) {
        if (!email && touched.email) {
          newErrors.email = "El correo es obligatorio";
          valid = false;
        } else if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          newErrors.email = "Correo inválido";
          valid = false;
        } else if (email.length > 50) {
          newErrors.email = "Máximo 50 caracteres";
          valid = false;
        }
      } else if (shouldShowUsername()) {
        if (!username && touched.username) {
          newErrors.username = "El usuario es obligatorio";
          valid = false;
        } else if (username.length > 50) {
          newErrors.username = "Máximo 50 caracteres";
          valid = false;
        } else if (username.length > 0 && username.length < 3) {
          newErrors.username = "Mínimo 3 caracteres";
          valid = false;
        }
      }
    }

    // Contraseña
    if (!password && touched.password) {
      newErrors.password = "La contraseña es obligatoria";
      valid = false;
    } else if (password.length > 50) {
      newErrors.password = "Máximo 50 caracteres";
      valid = false;
    }

    setErrors(newErrors);

    // Form is valid only if all required fields are present and no validation errors
    const currentIdentifier = getCurrentIdentifier();
    const formIsValid = currentIdentifier && password && role && valid;
    setIsValid(formIsValid);
    return formIsValid;
  };

  // Mostrar y auto-ocultar alertas de error
  useEffect(() => {
    const currentError = error || customError;
    if (currentError) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
        setCustomError("");
        clearError();
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [error, customError, clearError]);

  useEffect(() => {
    validate();
  }, [email, username, password, role, touched]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setTouched((prev) => ({ ...prev, email: true }));
    if (error || customError) {
      setShowAlert(false);
      setCustomError("");
      clearError();
    }
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setTouched((prev) => ({ ...prev, username: true }));
    if (error || customError) {
      setShowAlert(false);
      setCustomError("");
      clearError();
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setTouched((prev) => ({ ...prev, password: true }));
    if (error || customError) {
      setShowAlert(false);
      setCustomError("");
      clearError();
    }
  };

  /**
   * handleSubmit
   *
   * 1. Marca los campos como tocados según el rol.
   * 2. Valida el formulario.
   * 3. Llama a login() del contexto.
   * 4. Construye sessionJson con permisos del rol (vía getRoleConfig).
   * 5. Guarda sessionJson en localStorage y lo pasa como state en navigate.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Marcar campos como tocados según el rol
    const touchedFields = { password: true, role: true };
    if (shouldShowEmail()) touchedFields.email = true;
    if (shouldShowUsername()) touchedFields.username = true;
    setTouched((prev) => ({ ...prev, ...touchedFields }));

    if (!validate()) return;

    try {
      const identifier = getCurrentIdentifier();
      console.log(`Iniciando login para: ${identifier} con rol: ${role}`);

      // Llamada al auth context/login. Se asume que devuelve datos del usuario.
      const loginResult = await login({
        username: identifier,
        password: password,
        role: role,
      });

      // Obtener la configuración del rol desde roleRoutes (permisos, dashboardRoute, etc)
      const roleConfig = getRoleConfig(role);

      // Construir el JSON de sesión que el usuario pidió
      // Incluye: información del usuario (si loginResult trae user lo usamos), rol y permisos
      const sessionJson = {
        user: loginResult?.user ?? { identifier }, // fallback si loginResult no trae user
        role: role,
        permissions: Array.isArray(roleConfig.permissions) ? roleConfig.permissions : [],
        dashboardRoute: roleConfig.dashboardRoute || getRedirectAfterLogin(role, from),
        timestamp: new Date().toISOString(),
        rememberMe: !!rememberMe,
      };

      // Registro en consola para debugging (aquí "te dice el json que permisos tiene cada rol")
      console.log("Session JSON:", sessionJson);

      // Persistir en localStorage para que otras partes del cliente lo puedan leer
      try {
        localStorage.setItem("session", JSON.stringify(sessionJson));
      } catch (storageError) {
        // Si falló el storage, no interrumpimos el flujo de navegación
        console.warn("No fue posible guardar session en localStorage:", storageError);
      }

      // Obtener la ruta de redirección
      const redirectPath = getRedirectAfterLogin(role, from);

      // Redirigir y pasar el sessionJson en el state de navigate para acceso inmediato
      navigate(redirectPath, { replace: true, state: { session: sessionJson } });

    } catch (loginError) {
      console.error("Error en el proceso de login:", loginError);
      if (loginError.message && !loading) {
        setCustomError(loginError.message);
        setShowAlert(true);
      } else {
        setCustomError("Error desconocido en el login");
        setShowAlert(true);
      }
    }
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
    setCustomError("");
    clearError();
  };

  const currentError = error || customError;

  return (
    <CoverLayout image={bgImage}>
      {/* Alert positioned above the card */}
      <MDBox
        sx={{
          position: "fixed",
          top: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: "90%",
          maxWidth: "400px",
        }}
      >
        <Collapse in={showAlert}>
          <Alert
            severity="error"
            onClose={handleCloseAlert}
            sx={{
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              borderRadius: "8px",
            }}
          >
            Error de inicio de sesión: {currentError}
          </Alert>
        </Collapse>
      </MDBox>

      <Card>
        <MDBox
          variant="gradient"
          sx={{
            background: "#9B5DE5",
            borderRadius: "10px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            mx: 2,
            mt: -3,
            p: 2,
            mb: 1,
            textAlign: "center",
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Login
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {/* SELECTOR DE ROL */}
            <MDBox mb={2}>
              <FormControl fullWidth error={Boolean(errors.role)}>
                <InputLabel
                  id="rol-label"
                  sx={{
                    fontSize: "0.875rem",
                    color: errors.role ? "#f44336" : "#7b809a",
                    "&.Mui-focused": {
                      color: errors.role ? "#f44336" : "#9B5DE5",
                    },
                    "&.Mui-error": {
                      color: "#f44336",
                    },
                    "&:hover": {
                      color: errors.role ? "#f44336" : "#9B5DE5",
                    },
                  }}
                >
                  Seleccionar Rol
                </InputLabel>
                <Select
                  labelId="rol-label"
                  id="rol"
                  value={role}
                  label="Seleccionar Rol"
                  onChange={handleRoleChange}
                  disabled={loading}
                  sx={{
                    height: "45px",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    fontSize: "0.875rem",
                    color: "rgba(0, 0, 0, 0.87)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.role ? "#f44336" : "#d2d6da",
                      borderWidth: "1px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.role ? "#f44336" : "#9B5DE5",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: errors.role ? "#f44336" : "#9B5DE5",
                      borderWidth: "2px",
                    },
                    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#f44336",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <em>Seleccionar Rol</em>
                  </MenuItem>
                  <MenuItem value="admin">Super Admin</MenuItem>
                  <MenuItem value="empresa">Empresa</MenuItem>
                  <MenuItem value="evaluador">Distribuidor autorizado</MenuItem>
                  <MenuItem value="candidato">Talento en evaluación</MenuItem>
                </Select>
                {errors.role && (
                  <MDTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                    {errors.role}
                  </MDTypography>
                )}
              </FormControl>
            </MDBox>

            {/* CAMPOS DINÁMICOS */}
            {role && (
              <>
                {shouldShowEmail() && (
                  <MDBox mb={2}>
                    <MDInput
                      type="email"
                      label="Correo electrónico"
                      fullWidth
                      value={email}
                      onChange={handleEmailChange}
                      inputProps={{ maxLength: 50 }}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f44336",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.email ? "#f44336" : "#9B5DE5",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.email ? "#f44336" : "#9B5DE5",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                          color: errors.email ? "#f44336" : "#7b809a",
                          "&:hover": {
                            color: "#9B5DE5",
                          },
                          "&.Mui-focused": {
                            color: errors.email ? "#f44336" : "#9B5DE5",
                          },
                          "&.Mui-error": {
                            color: "#f44336",
                          },
                        },
                      }}
                    />
                  </MDBox>
                )}

                {shouldShowUsername() && (
                  <MDBox mb={2}>
                    <MDInput
                      type="text"
                      label="Usuario"
                      fullWidth
                      value={username}
                      onChange={handleUsernameChange}
                      inputProps={{ maxLength: 50 }}
                      error={Boolean(errors.username)}
                      helperText={errors.username}
                      disabled={loading}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#f44336",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.username ? "#f44336" : "#9B5DE5",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: errors.username ? "#f44336" : "#9B5DE5",
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                          color: errors.username ? "#f44336" : "#7b809a",
                          "&:hover": {
                            color: "#9B5DE5",
                          },
                          "&.Mui-focused": {
                            color: errors.username ? "#f44336" : "#9B5DE5",
                          },
                          "&.Mui-error": {
                            color: "#f44336",
                          },
                        },
                      }}
                    />
                  </MDBox>
                )}

                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="Contraseña"
                    fullWidth
                    value={password}
                    onChange={handlePasswordChange}
                    inputProps={{ maxLength: 50 }}
                    error={Boolean(errors.password)}
                    helperText={errors.password}
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#f44336",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.password ? "#f44336" : "#9B5DE5",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.password ? "#f44336" : "#9B5DE5",
                          borderWidth: "2px",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                        color: errors.password ? "#f44336" : "#7b809a",
                        "&:hover": {
                          color: "#9B5DE5",
                        },
                        "&.Mui-focused": {
                          color: errors.password ? "#f44336" : "#9B5DE5",
                        },
                        "&.Mui-error": {
                          color: "#f44336",
                        },
                      },
                    }}
                  />
                </MDBox>
              </>
            )}

            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} disabled={loading} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{
                  cursor: loading ? "not-allowed" : "pointer",
                  userSelect: "none",
                  ml: -1,
                  opacity: loading ? 0.6 : 1,
                }}
              >
                &nbsp;&nbsp;Acuérdate de mí
              </MDTypography>
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                fullWidth
                type="submit"
                disabled={!isValid || loading}
                sx={{
                  background: isValid && !loading ? "#9B5DE5" : "#FFFFFF",
                  color: isValid && !loading ? "#ffffffff" : "#000000",
                  cursor: isValid && !loading ? "pointer" : "not-allowed",
                  "&:hover": {
                    background: isValid && !loading ? "#54D38E" : "#FFFFFF",
                    color: isValid && !loading ? "#FFFFFF" : "#000000",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Iniciar sesión"}
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                ¿No tienes una cuenta?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  sx={{
                    color: "#9B5DE5",
                    fontWeight: "medium",
                    backgroundClip: "text",
                  }}
                >
                  Registrarse
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default Basic;
